import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notesApi } from '../api/notesApi';
import { CreateNoteRequest, UpdateNoteRequest, Note } from '../types/notes';
import { useUser } from '../contexts/UserContext';

export const useNotes = (userEmail: string) => {
  return useQuery({
    queryKey: ['notes', userEmail],
    queryFn: () => notesApi.getAllNotes(userEmail),
    enabled: !!userEmail,
  });
};

export const useNote = (id: number) => {
  return useQuery({
    queryKey: ['notes', id],
    queryFn: () => notesApi.getNote(id),
    enabled: !!id,
  });
};

export const useCreateNote = () => {
  const queryClient = useQueryClient();
  const { currentUser } = useUser();
  
  return useMutation({
    mutationFn: (data: CreateNoteRequest) => notesApi.createNote(data, currentUser?.email || ''),
    // Optimistic update for adding a note
    onMutate: async (data: CreateNoteRequest) => {
      await queryClient.cancelQueries({ queryKey: ['notes', currentUser?.email] });
      const previousNotes = queryClient.getQueryData<Note[]>(['notes', currentUser?.email]);
      
      // Generate a temporary optimistic note
      const optimisticNote: Note = {
        id: -Date.now(), // Use negative ID to avoid conflicts with real IDs
        title: data.title || "",
        content: data.content,
        color: data.color || "#FFE066",
        positionX: data.positionX || 100,
        positionY: data.positionY || 100,
        width: data.width || 250,
        height: data.height || 200,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        tags: data.tags || undefined,
        userId: currentUser?.email || '',
        isPublic: false,
        publicAccessToken: undefined
      };
      
      if (previousNotes) {
        queryClient.setQueryData(['notes', currentUser?.email], [optimisticNote, ...previousNotes]);
      }
      return { previousNotes };
    },
    onError: (_err, _data, context) => {
      // Rollback on error
      if (context?.previousNotes) {
        queryClient.setQueryData(['notes', currentUser?.email], context.previousNotes);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['notes', currentUser?.email] });
    },
  });
};

export const useUpdateNote = () => {
  const queryClient = useQueryClient();
  const { currentUser } = useUser();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateNoteRequest }) => 
      notesApi.updateNote(id, data, currentUser?.email || ''),
    // Optimistic update for editing a note
    onMutate: async ({ id, data }: { id: number; data: UpdateNoteRequest }) => {
      await queryClient.cancelQueries({ queryKey: ['notes', currentUser?.email] });
      const previousNotes = queryClient.getQueryData<Note[]>(['notes', currentUser?.email]);
      
      if (previousNotes) {
        const newNotes = previousNotes.map(note =>
          note.id === id ? { ...note, ...data, updatedAt: new Date().toISOString() } : note
        );
        queryClient.setQueryData(['notes', currentUser?.email], newNotes);
      }
      return { previousNotes };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousNotes) {
        queryClient.setQueryData(['notes', currentUser?.email], context.previousNotes);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['notes', currentUser?.email] });
    },
  });
};

export const useDeleteNote = () => {
  const queryClient = useQueryClient();
  const { currentUser } = useUser();
  
  return useMutation({
    mutationFn: (id: number) => notesApi.deleteNote(id),
    // Optimistic update for deleting a note
    onMutate: async (id: number) => {
      await queryClient.cancelQueries({ queryKey: ['notes', currentUser?.email] });
      const previousNotes = queryClient.getQueryData<Note[]>(['notes', currentUser?.email]);
      
      if (previousNotes) {
        const newNotes = previousNotes.filter(note => note.id !== id);
        queryClient.setQueryData(['notes', currentUser?.email], newNotes);
      }
      return { previousNotes };
    },
    onError: (_err, _id, context) => {
      if (context?.previousNotes) {
        queryClient.setQueryData(['notes', currentUser?.email], context.previousNotes);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['notes', currentUser?.email] });
    },
  });
};